"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import dynamic from "next/dynamic";
import { SuccessMessageType } from "@/interface/types";

/* One asset serves both breakpoints: a head-and-shoulders portrait that sits
   bottom-aligned in the desktop column and bottom-right behind the mobile
   copy. Kept as a single URL so the two <Image> instances share a fetch. */
const BANNER_IMAGE =
  "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-course/cpa-course-banner.webp";

const EnqueryForm = dynamic(() => import("@/components/forms/EnqueryForm"), {
  ssr: false,
  loading: () => <FormPlaceholder />,
});

const FormPlaceholder = () => (
  <div className="flex flex-col gap-3 min-h-[300px] animate-pulse">
    <div className="h-11 w-full rounded-lg bg-[#d8e8f5]" />
    <div className="h-11 w-full rounded-lg bg-[#d8e8f5]" />
    <div className="h-11 w-full rounded-lg bg-[#d8e8f5]" />
    <div className="h-4 w-3/4 rounded bg-[#d8e8f5] mt-1" />
    <div className="h-11 w-full rounded-lg bg-[#b8d4f0] mt-2" />
  </div>
);

/*
 * Angled "flag" label, mobile only — desktop uses the plain two-tone eyebrow.
 *
 * Two stacked shapes: the outer solid navy reads as a 6px border around the
 * inner gradient. The arrow head is redrawn for this label's width rather than
 * scaled from the demo's 120px version, because stretching the viewBox would
 * skew the diagonal. "CPA COURSE" needs ~111px of run (12px bold, 2px tracking,
 * 16px inset), so 136px leaves the text clear of the shoulder.
 */
const CourseTag = ({ label }: { label: string }) => (
  <span className="md:hidden relative inline-flex items-center shrink-0 h-[30px] w-[136px] -ml-4 mb-2">
    <svg
      className="absolute left-0 top-0"
      width="136"
      height="30"
      viewBox="0 0 136 30"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M0 0H123.536L136 15L123.536 30H0V0Z" fill="#336297" />
    </svg>
    <svg
      className="absolute left-0 top-0"
      width="130"
      height="30"
      viewBox="0 0 130 30"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M0 0H118.059L130 15L118.059 30H0V0Z"
        fill="url(#cpa_course_tag)"
      />
      <defs>
        <linearGradient
          id="cpa_course_tag"
          x1="0"
          y1="15"
          x2="126"
          y2="15"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#164B7F" />
          <stop offset="1" stopColor="#0B2A4D" />
        </linearGradient>
      </defs>
    </svg>
    <span className="relative z-10 pl-4 text-white text-[12px] font-bold uppercase tracking-[2px] leading-none">
      {label}
    </span>
  </span>
);

const CPACourseBanner = () => {
  const [mountForm, setMountForm] = useState(false);

  useEffect(() => {
    const win = window as Window & {
      requestIdleCallback?: (
        _cb: () => void,
        _opts?: { timeout: number },
      ) => number;
      cancelIdleCallback?: (_id: number) => void;
    };
    if (typeof win.requestIdleCallback === "function") {
      const id = win.requestIdleCallback(() => setMountForm(true), {
        timeout: 2000,
      });
      return () => win.cancelIdleCallback?.(id);
    }
    const id = window.setTimeout(() => setMountForm(true), 1200);
    return () => window.clearTimeout(id);
  }, []);

  return (
    <section className="w-full bg-white md:pb-6 md:pt-16 ">
      <div className=" md:max-w-[1340px] lg:px-7 mx-auto">
        {/* ── Gradient rounded card ── */}
        <div
          className="overflow-hidden md:rounded-[50px]"
          style={{
            background: "linear-gradient(270deg, #CEE6FF 0%, #F1F8FF 100%)",
          }}
        >
          <div
            className="md:grid flex flex-wrap md:items-stretch md:min-h-[380px]"
            style={{
              gridTemplateColumns:
                "minmax(0,1.35fr) minmax(280px,1.3fr) minmax(320px,1.39fr)",
            }}
          >
            {/*
             * LEFT: copy. On mobile this block also hosts the portrait, pinned
             * to its bottom-right corner, with pr-[34%] keeping the text clear
             * of it — that overlap is why the image cannot be a sibling.
             */}
            <div className="flex flex-col justify-center items-start text-left px-4 md:px-10 pt-7 pb-2 md:py-10 order-1 max-md:relative max-md:w-full max-md:pr-[34%]">
              <CourseTag label="CPA COURSE" />
              <p className="hidden md:block text-[14px] md:text-[16px] font-bold uppercase tracking-[2px] mb-2">
                <span className="text-[#39F]">CPA</span>{" "}
                <span className="text-[#333]">COURSE</span>
              </p>
              <h1 className="text-[#3399FF] text-[22px] md:text-[24px] font-bold leading-[25px] mb-2 md:mb-[14px]">
                Be the
                <br className="md:hidden block" /> AI-Ready CPA
                <br className="md:hidden block" /> firms
                <br className="hidden md:block" /> want to hire
              </h1>
              <p className="text-[#333] text-sm md:leading-normal leading-[1.3] max-w-[calc(54vw_-_34px)] md:max-w-[465px]">
                Enrolling in the U.S. CPA course at Miles means earning a
                world-class credential fully integrated with modern AI
                workflows. Discover course details, eligibility, fees, and your
                fast-track route to a global career!
              </p>
              <div className="md:hidden absolute bottom-0 right-0 w-[50%]">
                <Image
                  src={BANNER_IMAGE}
                  alt="AI-Ready CPA"
                  width={402}
                  height={420}
                  className="object-contain w-full h-auto"
                  priority
                />
              </div>
            </div>

            {/* ── CENTER: banner image (desktop) ── */}
            <div className="hidden md:flex items-end justify-center order-2 self-end min-w-0">
              <Image
                src={BANNER_IMAGE}
                alt="AI-Ready CPA"
                width={382}
                height={400}
                className="object-contain object-bottom w-auto max-w-full h-[370px] lg:h-[400px] block"
                priority
              />
            </div>

            {/* ── RIGHT: form card ── */}
            <div className="flex items-center justify-center order-3 px-4 md:px-6 py-4 md:py-8 max-md:w-full max-md:pt-0">
              <div className="bg-white rounded-2xl border border-[#DEE7F5] shadow-sm p-5 md:p-5 w-full max-w-[420px] md:max-w-none">
                {mountForm ? (
                  <EnqueryForm
                    pageName="CPA"
                    courseId={1}
                    darkBackground={false}
                    submitButtonName="Request a Callback"
                    formType="Banner Form"
                    isAStepperForm
                    successMessage={SuccessMessageType.ACCOUNTING}
                  />
                ) : (
                  <FormPlaceholder />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CPACourseBanner;
